/**
 * Note: Browser extensions (like LanguageTool, Grammarly, etc.) may wrap window.fetch
 * and log "TypeError: Failed to fetch" errors to the console. These are caught and
 * handled by our error handling, and fallbackData is used when configured.
 * The console errors are from the extension's wrapper, not application failures.
 */
export type RobustFetchInit = RequestInit & {
  // If provided, this callback will be invoked on a 401 response and can
  // attempt to refresh tokens. If it returns true the request will be retried once.
  onAuthFailure?: () => Promise<boolean>;
  // Optional convenience fields used by robustFetchJSON (ignored by the core robustFetch)
  retries?: number;
  timeout?: number;
  fallbackData?: any;
  // Internal flag used by some callers to skip browser extension compatibility checks
  skipExtensionCheck?: boolean;
};

// Core default export
export default async function robustFetch(
  input: RequestInfo,
  init?: RobustFetchInit,
): Promise<Response> {
  const token =
    typeof window !== "undefined"
      ? localStorage.getItem("auth_token") || localStorage.getItem("api_key")
      : null;

  const headers = new Headers(init?.headers as HeadersInit | undefined);
  if (token && !headers.has("Authorization")) {
    headers.set("Authorization", `Bearer ${token}`);
  }

  // If body is a plain object, stringify it and set JSON header when not FormData
  let body: any = init?.body;
  if (
    body &&
    typeof body === "object" &&
    !(body instanceof FormData) &&
    !(body instanceof URLSearchParams)
  ) {
    try {
      body = JSON.stringify(body);
      if (!headers.has("Content-Type"))
        headers.set("Content-Type", "application/json");
    } catch (e) {
      // leave body as-is
    }
  }

  const merged: RequestInit = {
    ...init,
    headers,
    body,
  };

  const retries = (init as any)?.retries ?? 1;
  const timeout = (init as any)?.timeout ?? 0;

  const doFetch = async (): Promise<Response> => {
    if (timeout > 0) {
      const controller = new AbortController();
      const tid = setTimeout(() => {
        controller.abort(new DOMException(`Request timed out after ${timeout}ms`, "TimeoutError"));
      }, timeout);
      try {
        return await fetch(input, { ...merged, signal: controller.signal });
      } finally {
        clearTimeout(tid);
      }
    }
    return fetch(input, merged);
  };

  let lastErr: unknown;
  let res: Response | undefined;

  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      res = await doFetch();
      break; // success
    } catch (err: any) {
      // Normalize AbortError/TimeoutError into a NetworkError so callers
      // get a consistent error type and the raw DOMException isn't surfaced.
      if (err?.name === "AbortError" || err?.name === "TimeoutError") {
        lastErr = new NetworkError(
          err?.message || "Request timed out",
          "abort",
        );
      } else {
        lastErr = err;
      }
      if (attempt < retries) {
        await new Promise((r) => setTimeout(r, 300 * attempt));
      }
    }
  }

  if (!res) throw lastErr ?? new Error("Failed to fetch");

  if (res.status === 401 && init?.onAuthFailure) {
    try {
      const refreshed = await init.onAuthFailure();
      if (refreshed) {
        // try again once
        const token2 =
          typeof window !== "undefined"
            ? localStorage.getItem("auth_token") ||
              localStorage.getItem("api_key")
            : null;
        if (token2) headers.set("Authorization", `Bearer ${token2}`);
        res = await doFetch();
      }
    } catch (e) {
      // swallow and return original 401
    }
  }

  return res;
}

// Export the default function also as a named export so callers can use either import styles
export { robustFetch };

// NetworkError and helpers
export class NetworkError extends Error {
  public type?: string;
  constructor(message?: string, type?: string) {
    super(message || "Network error");
    this.name = "NetworkError";
    this.type = type;
  }
}

export function isNetworkError(e: unknown): boolean {
  if (!e) return false;
  if (e instanceof NetworkError) return true;
  const anyE = e as any;
  if (anyE && typeof anyE === "object") {
    if (anyE.name === "AbortError") return true;
    if (anyE.name === "TypeError" && anyE.message === "Failed to fetch")
      return true;
    if (
      typeof anyE.type === "string" &&
      (anyE.type === "network" || anyE.type === "abort")
    )
      return true;
    if (
      typeof anyE.message === "string" &&
      /network|failed to fetch|fetch failed|timeout|abort|load failed/i.test(
        anyE.message,
      )
    )
      return true;
  }
  return false;
}

// Convenience helper to fetch JSON with retries, timeout, and fallback support
export async function robustFetchJSON(
  url: RequestInfo,
  init?: RobustFetchInit & {
    retries?: number;
    timeout?: number;
    fallbackData?: any;
  },
): Promise<any> {
  const retries = init?.retries ?? 1;
  const timeout = init?.timeout;
  const fallbackData = init?.fallbackData;

  let lastErr: any = null;

  for (let attempt = 1; attempt <= retries; attempt++) {
    const controller = new AbortController();
    const signal = controller.signal;
    let timeoutId: any = undefined;
    if (timeout && timeout > 0) {
      // Abort the request after timeout to prevent hanging
      // If fallbackData is configured, the AbortError will be caught
      // and fallback data returned instead of throwing
      timeoutId = setTimeout(() => {
        controller.abort(
          new DOMException(
            `Request timeout after ${timeout}ms. The server may be experiencing high load. Please try again.`,
            "TimeoutError",
          ),
        );
      }, timeout);
    }

    try {
      const mergedInit: RobustFetchInit = { ...(init || {}), signal };
      // Remove helper-only fields to avoid passing them down
      delete (mergedInit as any).retries;
      delete (mergedInit as any).timeout;
      delete (mergedInit as any).fallbackData;

      const res = await robustFetch(url, mergedInit).catch((err) => {
        // If this is a browser extension error or network error and we have fallback data,
        // throw it to be caught by the outer catch which will return the fallback
        if (fallbackData !== undefined && isNetworkError(err)) {
          // Convert to NetworkError for consistent handling
          throw new NetworkError(
            err instanceof Error ? err.message : "Network error",
            "network",
          );
        }
        throw err;
      });
      if (timeoutId) clearTimeout(timeoutId);

      if (!res) throw new NetworkError("No response received", "network");
      if (!res.ok) {
        // Try parse body for better error message
        let text: string | null = null;
        try {
          text = await res.text();
        } catch (e) {
          // ignore
        }
        const errMsg = text || `HTTP ${res.status} ${res.statusText}`;

        // If caller provided fallbackData, return it for common non-fatal statuses
        if (
          fallbackData !== undefined &&
          (res.status === 401 || res.status === 404 || res.status >= 500)
        ) {
          console.warn(
            `robustFetchJSON: returning fallbackData for ${String(url)} status=${res.status}`,
          );
          return fallbackData;
        }

        const err = new Error(errMsg);
        (err as any).status = res.status;
        throw err;
      }

      // parse JSON safely
      const json = await res.json().catch(() => null);
      return json;
    } catch (err) {
      if (timeoutId) clearTimeout(timeoutId);
      lastErr = err;

      // Normalize various error types to NetworkError
      if ((err as any)?.name === "AbortError" || (err as any)?.name === "TimeoutError") {
        const msg = (err as any)?.message || "Request timed out";
        lastErr = new NetworkError(msg, "abort");
      } else if (
        (err as any)?.name === "TypeError" &&
        (err as any)?.message === "Failed to fetch"
      ) {
        // Browser extension or network error - common and expected
        lastErr = new NetworkError("Failed to fetch", "network");
      } else if (err instanceof NetworkError) {
        // Already normalized
        lastErr = err;
      } else if (
        err instanceof Error &&
        /timeout|network|failed to fetch|abort|load failed/i.test(err.message)
      ) {
        lastErr = new NetworkError(err.message, "network");
      }

      // If this is a network error and we have fallback data, use it on final attempt
      if (
        fallbackData !== undefined &&
        isNetworkError(lastErr) &&
        attempt >= retries
      ) {
        return fallbackData;
      }

      if (attempt < retries) {
        // simple backoff before retry
        await new Promise((r) => setTimeout(r, 250 * attempt));
        continue;
      }

      // Final attempt failed - return fallback if configured, otherwise throw
      if (fallbackData !== undefined) return fallbackData;
      throw lastErr;
    }
  }

  if (fallbackData !== undefined) return fallbackData;
  throw lastErr || new Error("Unknown fetch error");
}
